import { useState } from 'react'
import { useRealtimeMetrics } from '@/hooks/useRealtime'
import { useCampaignSelection } from '@/entities/campaign'
import { CampaignSelector } from '@/components/features/CampaignSelector'
import { SummaryStatsCard, RealTimeCharts, ChartControls } from './ui'

export function Realtime() {
  const [days, setDays] = useState(2)
  const [stepSeconds, setStepSeconds] = useState(1800)
  const [refreshSeconds, setRefreshSeconds] = useState(30)
  const { campaignId, setCampaignId, campaigns } = useCampaignSelection()

  const { data, isLoading, isFetching } = useRealtimeMetrics({
    stepSeconds,
    days,
    campaignId,
    refetchIntervalMs: refreshSeconds * 1000,
  })

  return (
    <div className="w-full h-full p-6 overflow-y-auto">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-heading font-bold mb-1">Real-time Monitoring</h1>
          <p className="text-body text-muted-foreground">Live call metrics with summary for last 2 hours</p>
        </div>
        <CampaignSelector
          campaigns={campaigns}
          selectedCampaignId={campaignId}
          onCampaignChange={setCampaignId}
          className="w-full sm:w-72"
          showAllOption
        />
      </div>

      {/* Chart Controls */}
      <ChartControls
        days={days}
        stepSeconds={stepSeconds}
        refreshSeconds={refreshSeconds}
        onDaysChange={setDays}
        onStepSecondsChange={setStepSeconds}
        onRefreshSecondsChange={setRefreshSeconds}
        isLoading={isFetching}
      />

      {isLoading && !data ? (
        <>
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border">
            <div className="animate-pulse">
              <div className="h-6 bg-muted rounded w-48 mb-4"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-24 bg-muted rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm p-6 border">
                <div className="animate-pulse">
                  <div className="h-4 bg-muted rounded w-32 mx-auto mb-4"></div>
                  <div className="h-[300px] bg-muted rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div>
          {/* Summary Stats */}
          <SummaryStatsCard
            summaryTotalCalls={data?.summaryTotalCalls}
            summarySuccessfulCalls={data?.summarySuccessfulCalls}
            summaryConnectedCalls={data?.summaryConnectedCalls}
            summaryCallsOver30s={data?.summaryCallsOver30s}
          />

          {/* Charts Section */}
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <div className="h-px flex-1 bg-border"></div>
              <h2 className="px-4 text-heading font-bold">Real-time Charts</h2>
              <div className="h-px flex-1 bg-border"></div>
            </div>

            {data?.totalCalls && data.totalCalls.length > 0 ? (
              <RealTimeCharts
                totalCalls={data.totalCalls}
                successfulCalls={data.successfulCalls}
                connectedCalls={data.connectedCalls}
                callsOver30s={data.callsOver30s}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 border text-center">
                <p className="text-muted-foreground text-body">No call data for the selected time window.</p>
                <p className="text-muted-foreground text-detail mt-1">Charts will appear once calls are recorded.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
